import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DashboardLayout from "../components/Layout/DashboardLayout";
import { clientsApi } from "../api/clientsApi";
import { ClientScoreSummary, scoresApi } from "../api/scoresApi";
import { disputesApi } from "../api/disputesApi";
import { Client, DisputeItem, ScoreHistoryPoint } from "../types";

const cardClass = "rounded-xl border border-slate-800 bg-slate-900/90 p-4 shadow-lg shadow-black/20";
const selectClass =
  "rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none ring-emerald-500/50 focus:ring-2";
const inputClass =
  "mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none ring-emerald-500/50 focus:ring-2";
function bureauKey(b: string): "equifax" | "experian" | "transUnion" {
  const x = b.toLowerCase();
  if (x.includes("equifax")) return "equifax";
  if (x.includes("experian")) return "experian";
  return "transUnion";
}

function avgThree(eq: number, ex: number, tu: number): number {
  return Math.round((eq + ex + tu) / 3);
}

function parseImpact(estimated: string | undefined): number {
  if (!estimated?.trim()) return 8;
  const nums = estimated.match(/\d+/g);
  if (!nums?.length) return 8;
  const vals = nums.map(Number);
  return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
}

function isActiveDispute(d: DisputeItem): boolean {
  const s = d.status.toLowerCase();
  return s.includes("dispute") && !s.includes("deleted") && !s.includes("resolved") && !s.includes("closed");
}

const ScoreTrackerPage: React.FC = () => {
  const rangeDays = 365 * 3;
  const [clients, setClients] = useState<Client[]>([]);
  const [summaries, setSummaries] = useState<ClientScoreSummary[]>([]);
  const [clientId, setClientId] = useState("");
  const [points, setPoints] = useState<ScoreHistoryPoint[]>([]);
  const [disputes, setDisputes] = useState<DisputeItem[]>([]);
  const [goalScore, setGoalScore] = useState(720);
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState("");
  const [formDate, setFormDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [formEq, setFormEq] = useState("650");
  const [formEx, setFormEx] = useState("650");
  const [formTu, setFormTu] = useState("650");
  const [formNotes, setFormNotes] = useState("");
  const [formBusy, setFormBusy] = useState(false);

  const pickDefaultClientId = useCallback((cls: Client[], sums: ClientScoreSummary[]) => {
    const sorted = [...sums].sort((a, b) => {
      const ta = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
      const tb = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
      return tb - ta;
    });
    if (sorted.length > 0 && cls.some((c) => c.id === sorted[0].clientId)) {
      return sorted[0].clientId;
    }
    return cls[0]?.id ?? "";
  }, []);

  const loadClients = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [cls, sums] = await Promise.all([clientsApi.getAll(), scoresApi.getSummary()]);
      setClients(cls);
      setSummaries(sums);
      setClientId((prev) => {
        if (prev && cls.some((c) => c.id === prev)) return prev;
        return pickDefaultClientId(cls, sums);
      });
    } catch {
      setError("Could not load clients.");
    } finally {
      setLoading(false);
    }
  }, [pickDefaultClientId]);

  useEffect(() => {
    void loadClients();
  }, [loadClients]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === clientId) ?? null,
    [clients, clientId],
  );

  const reloadSeries = useCallback(async () => {
    if (!clientId) {
      setPoints([]);
      setDisputes([]);
      return;
    }
    setChartLoading(true);
    setError("");
    try {
      const [hist, disp] = await Promise.all([
        scoresApi.getHistory(clientId, rangeDays),
        disputesApi.getAllForClient(clientId).catch(() => []),
      ]);
      setPoints(hist);
      setDisputes(disp);
    } catch {
      setError("Could not load score history.");
      setPoints([]);
      setDisputes([]);
    } finally {
      setChartLoading(false);
    }
  }, [clientId, rangeDays]);

  useEffect(() => {
    void reloadSeries();
  }, [reloadSeries]);

  const chartRows = useMemo(() => {
    const byDate = new Map<
      string,
      { equifax?: number; experian?: number; transUnion?: number; projected?: number }
    >();

    const sorted = [...points].sort((a, b) => a.period.localeCompare(b.period));
    for (const p of sorted) {
      const row = byDate.get(p.period) ?? {};
      row[bureauKey(p.bureau)] = p.score;
      byDate.set(p.period, row);
    }

    const rows = Array.from(byDate.entries())
      .map(([date, v]) => ({ date, ...v }))
      .sort((a, b) => a.date.localeCompare(b.date));

    if (!selectedClient) {
      return rows;
    }

    const active = disputes.filter(isActiveDispute);
    const totalGain = active.reduce((sum, d) => sum + parseImpact(d.estimatedScoreImpact), 0);
    const cappedGain = Math.min(120, Math.max(0, totalGain));
    const currentMid = avgThree(
      selectedClient.equifaxScore,
      selectedClient.experianScore,
      selectedClient.transUnionScore,
    );
    const today = new Date();
    for (let i = 1; i <= 6; i += 1) {
      const t = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + i, 1));
      const label = t.toISOString().slice(0, 10);
      const step = Math.round(currentMid + (cappedGain * i) / 6);
      const row = byDate.get(label) ?? {};
      row.projected = Math.min(850, step);
      byDate.set(label, row);
    }

    return Array.from(byDate.entries())
      .map(([date, v]) => ({ date, ...v }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [points, disputes, selectedClient]);

  const summary = useMemo(() => {
    if (!selectedClient) {
      return { start: 0, current: 0, goal: goalScore };
    }
    const sorted = [...points].sort((a, b) => a.period.localeCompare(b.period));
    let start = 0;
    if (sorted.length > 0) {
      const first = sorted[0].period;
      const firstPts = sorted.filter((p) => p.period === first);
      if (firstPts.length) {
        start = Math.round(firstPts.reduce((s, p) => s + p.score, 0) / firstPts.length);
      }
    }
    if (start === 0 && sorted.length === 0) {
      start = avgThree(
        selectedClient.equifaxScore,
        selectedClient.experianScore,
        selectedClient.transUnionScore,
      );
    }
    const current = avgThree(
      selectedClient.equifaxScore,
      selectedClient.experianScore,
      selectedClient.transUnionScore,
    );
    return { start, current, goal: goalScore };
  }, [points, selectedClient, goalScore]);

  useEffect(() => {
    if (!selectedClient) return;
    setFormEq(String(selectedClient.equifaxScore));
    setFormEx(String(selectedClient.experianScore));
    setFormTu(String(selectedClient.transUnionScore));
  }, [selectedClient]);

  const summaryRows = useMemo(() => {
    return [...summaries].sort((a, b) => {
      const ta = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
      const tb = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
      return tb - ta;
    });
  }, [summaries]);

  const tableRows = useMemo(() => {
    return [...points].sort((a, b) => {
      const c = b.period.localeCompare(a.period);
      if (c !== 0) return c;
      return a.bureau.localeCompare(b.bureau);
    });
  }, [points]);

  const onAddScore = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId) return;
    const eq = Number(formEq);
    const ex = Number(formEx);
    const tu = Number(formTu);
    const scores = [eq, ex, tu];
    if (scores.some((n) => Number.isNaN(n) || n < 300 || n > 850)) {
      setError("Each score must be between 300 and 850.");
      return;
    }
    const recorded = new Date(`${formDate}T12:00:00.000Z`).toISOString();
    setFormBusy(true);
    setError("");
    try {
      await scoresApi.addSnapshot({
        clientId,
        equifaxScore: eq,
        experianScore: ex,
        transUnionScore: tu,
        recordedDate: recorded,
        notes: formNotes.trim() || undefined,
      });
      await loadClients();
      await reloadSeries();
    } catch {
      setError("Could not save score entry.");
    } finally {
      setFormBusy(false);
    }
  };

  const formatDelta = (d: number | null | undefined) => {
    if (d === null || d === undefined) return "—";
    const sign = d > 0 ? "+" : "";
    return `${sign}${d}`;
  };

  const formatWhen = (iso: string | null | undefined) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString();
    } catch {
      return iso;
    }
  };

  if (loading && !clients.length) {
    return (
      <DashboardLayout>
        <div className={cardClass}>
          <p className="text-sm text-slate-400">Loading…</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-white">Score tracker</h1>
            <p className="text-sm text-slate-400">
              Score history from your ledger, tri-bureau snapshots, and dispute-based projections.
            </p>
          </div>
          <label className="text-xs text-slate-500">
            Selected client
            <select
              className={`${selectClass} mt-1 block min-w-[12rem]`}
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
            >
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.firstName} {c.lastName}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className={cardClass}>
          <h2 className="mb-2 text-sm font-semibold text-emerald-300">All clients</h2>
          <p className="mb-3 text-xs text-slate-500">
            Current scores on file, change vs. earliest snapshot, last score update. Click a row to open detail.
          </p>
          <div className="max-h-72 overflow-auto rounded-lg border border-slate-800">
            <table className="min-w-full text-left text-sm text-slate-300">
              <thead className="sticky top-0 bg-slate-950/95 text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">Client</th>
                  <th className="px-3 py-2 text-right">EQ</th>
                  <th className="px-3 py-2 text-right">EX</th>
                  <th className="px-3 py-2 text-right">TU</th>
                  <th className="px-3 py-2 text-right">Δ avg</th>
                  <th className="px-3 py-2">Updated</th>
                </tr>
              </thead>
              <tbody>
                {summaryRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                      No clients yet.
                    </td>
                  </tr>
                ) : (
                  summaryRows.map((s) => (
                    <tr
                      key={s.clientId}
                      role="button"
                      tabIndex={0}
                      onClick={() => setClientId(s.clientId)}
                      onKeyDown={(ev) => {
                        if (ev.key === "Enter" || ev.key === " ") {
                          ev.preventDefault();
                          setClientId(s.clientId);
                        }
                      }}
                      className={`cursor-pointer border-t border-slate-800/80 hover:bg-slate-800/50 ${s.clientId === clientId ? "bg-emerald-950/30" : ""}`}
                    >
                      <td className="px-3 py-2 font-medium text-slate-100">{s.clientName}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.currentEquifax}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.currentExperian}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{s.currentTransUnion}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-300">{formatDelta(s.deltaAverage)}</td>
                      <td className="px-3 py-2 text-slate-400">{formatWhen(s.lastUpdated)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-950/30 px-4 py-3 text-sm text-amber-100">
            {error}
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-3">
          {[
            ["Starting (earliest snapshot)", summary.start],
            ["Current (client profile avg.)", summary.current],
            ["Goal", summary.goal],
          ].map(([label, val]) => (
            <div key={String(label)} className={`${cardClass} border-emerald-900/40`}>
              <p className="text-xs uppercase tracking-wide text-emerald-400/90">{label}</p>
              <p className="mt-2 text-3xl font-semibold text-emerald-300">{val as number}</p>
            </div>
          ))}
        </div>

        <div className={cardClass}>
          <label className="text-xs text-slate-500">
            Goal score (target)
            <input
              type="number"
              min={300}
              max={850}
              className={inputClass}
              value={goalScore}
              onChange={(e) => setGoalScore(Number(e.target.value) || 0)}
            />
          </label>
        </div>

        <div className={`${cardClass} min-h-[22rem]`}>
          <h2 className="mb-2 text-sm font-semibold text-emerald-300">Bureau scores over time</h2>
          <p className="mb-3 text-xs text-slate-500">
            Solid lines: Equifax / Experian / TransUnion. Dashed: projected average from active disputes.
          </p>
          {chartLoading ? (
            <p className="py-16 text-center text-sm text-slate-500">Loading chart…</p>
          ) : chartRows.length === 0 ? (
            <p className="py-16 text-center text-sm text-slate-500">Select a client with score history.</p>
          ) : (
            <div className="h-80 w-full min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartRows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="date" tick={{ fill: "#94a3b8", fontSize: 11 }} minTickGap={24} />
                  <YAxis domain={[300, 850]} tick={{ fill: "#94a3b8", fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#020617",
                      border: "1px solid #065f46",
                      borderRadius: "8px",
                    }}
                    labelStyle={{ color: "#ecfdf5" }}
                  />
                  <Legend wrapperStyle={{ color: "#94a3b8", fontSize: 12 }} />
                  <Line
                    type="monotone"
                    dataKey="equifax"
                    name="Equifax"
                    stroke="#34d399"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "#34d399" }}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="experian"
                    name="Experian"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "#10b981" }}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="transUnion"
                    name="TransUnion"
                    stroke="#6ee7b7"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "#6ee7b7" }}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey="projected"
                    name="Projected (disputes)"
                    stroke="#4ade80"
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    dot={false}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className={cardClass}>
            <h2 className="mb-3 text-sm font-semibold text-emerald-300">Add score update</h2>
            <p className="mb-3 text-xs text-slate-500">
              Records Equifax, Experian, and TransUnion for the same month (first day of month is used). Updates the
              client profile scores.
            </p>
            <form onSubmit={(e) => void onAddScore(e)} className="space-y-3">
              <label className="block text-xs text-slate-500">
                Date
                <input
                  type="date"
                  className={inputClass}
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                  required
                />
              </label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <label className="block text-xs text-slate-500">
                  Equifax
                  <input
                    type="number"
                    min={300}
                    max={850}
                    className={inputClass}
                    value={formEq}
                    onChange={(e) => setFormEq(e.target.value)}
                    required
                  />
                </label>
                <label className="block text-xs text-slate-500">
                  Experian
                  <input
                    type="number"
                    min={300}
                    max={850}
                    className={inputClass}
                    value={formEx}
                    onChange={(e) => setFormEx(e.target.value)}
                    required
                  />
                </label>
                <label className="block text-xs text-slate-500">
                  TransUnion
                  <input
                    type="number"
                    min={300}
                    max={850}
                    className={inputClass}
                    value={formTu}
                    onChange={(e) => setFormTu(e.target.value)}
                    required
                  />
                </label>
              </div>
              <label className="block text-xs text-slate-500">
                Notes (optional)
                <input
                  type="text"
                  className={inputClass}
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder="e.g. After round 2 disputes"
                />
              </label>
              <button
                type="submit"
                disabled={formBusy || !clientId}
                className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-40"
              >
                {formBusy ? "Saving…" : "Save score update"}
              </button>
            </form>
          </div>

          <div className={cardClass}>
            <h2 className="mb-3 text-sm font-semibold text-slate-200">Score history</h2>
            <div className="max-h-80 overflow-auto rounded-lg border border-slate-800">
              <table className="min-w-full text-left text-sm text-slate-300">
                <thead className="sticky top-0 bg-slate-950/95 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-3 py-2">Period</th>
                    <th className="px-3 py-2">Bureau</th>
                    <th className="px-3 py-2 text-right">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-3 py-6 text-center text-slate-500">
                        No rows yet — add an entry or import history.
                      </td>
                    </tr>
                  ) : (
                    tableRows.map((r, i) => (
                      <tr key={`${r.period}-${r.bureau}-${i}`} className="border-t border-slate-800/80">
                        <td className="px-3 py-2 text-slate-400">{r.period}</td>
                        <td className="px-3 py-2 text-emerald-200/90">{r.bureau}</td>
                        <td className="px-3 py-2 text-right font-medium text-emerald-300">{r.score}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ScoreTrackerPage;
